import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, MessageSquare, Sparkles, Lightbulb, Image, ChevronDown, X, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TopNav } from "../components/layout/top-nav";
import { useAuth } from "../hooks/use-auth";
import { CreatePersonaModal } from "../components/create-persona-modal";
import { ImageUpload } from "../components/image-upload";
import { ConfirmDialog } from "../components/confirm-dialog";
import { LandingPage } from "../components/landing-page";

const models = [
  { id: "deepseek-v3", name: "DeepSeek V3", tagKey: "models.textChat", available: true },
  { id: "deepseek-v2", name: "DeepSeek V2", tagKey: "models.textChat", available: true },
  { id: "gpt-4o", name: "GPT-4o", tagKey: "models.imageUnderstanding", available: true },
  { id: "gpt-4o-mini", name: "GPT-4o Mini", tagKey: "models.imageUnderstanding", available: true },
  { id: "qwen-vl-max", name: "Qwen VL", tagKey: "models.imageUnderstanding", available: false },
  { id: "glm-4v", name: "GLM-4V", tagKey: "models.imageUnderstanding", available: false },
  { id: "hunyuan", name: "Hunyuan", tagKey: "models.textChat", available: false },
];

const defaultPersonas = [
  { id: "pm", icon: "📝", name: "产品经理", role: "需求分析 · 文档撰写", description: "帮你梳理产品逻辑，撰写专业PRD", model: "deepseek-v3" },
  { id: "designer", icon: "🎨", name: "设计师", role: "创意灵感 · 视觉方案", description: "品牌设计、UI建议、创意发散", model: "deepseek-v3" },
  { id: "developer", icon: "💻", name: "程序员", role: "代码优化 · 技术实现", description: "代码审查、重构建议、技术方案", model: "deepseek-v3" },
  { id: "analyst", icon: "📊", name: "分析师", role: "数据分析 · 商业洞察", description: "商业模式分析、数据解读、策略建议", model: "deepseek-v3" },
  { id: "mentor", icon: "🎓", name: "导师", role: "面试辅导 · 技能提升", description: "模拟面试、职业规划、学习路径", model: "deepseek-v3" },
  { id: "translator", icon: "🌐", name: "翻译官", role: "多语言 · 本地化", description: "精准翻译、文化适配、润色优化", model: "deepseek-v3" },
];

const defaultRecentChats = [
  { id: "1", title: "产品需求文档讨论", date: "2小时前", preview: "我们需要重新设计用户注册流程..." },
  { id: "2", title: "代码审查助手", date: "昨天", preview: "这段代码可以优化一下性能..." },
  { id: "3", title: "商业模式分析", date: "3天前", preview: "SaaS模式的优势在于..." },
  { id: "4", title: "面试准备", date: "1周前", preview: "请介绍一下你的项目经验..." },
];

export function HomePage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [selectedModel, setSelectedModel] = useState(models[0]);
  const [selectedPersona, setSelectedPersona] = useState<typeof defaultPersonas[0] | null>(null);
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [customPersonas, setCustomPersonas] = useState<typeof defaultPersonas>([]);
  const [deletedDefaultIds, setDeletedDefaultIds] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState<{ url: string; base64: string } | null>(null);
  const [recentChats, setRecentChats] = useState(defaultRecentChats);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; title: string } | null>(null);

  // 等待认证加载完成
  if (auth.loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // 未登录用户显示 Landing Page
  if (!auth.isSignedIn) {
    return <LandingPage />;
  }

  // 从本地存储加载自定义人格和已删除的预设人格
  useEffect(() => {
    const saved = localStorage.getItem("customPersonas");
    const deletedDefaults = JSON.parse(localStorage.getItem("deletedDefaultPersonas") || "[]");
    
    if (saved) {
      try {
        setCustomPersonas(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to load custom personas:", e);
      }
    }
    
    setDeletedDefaultIds(deletedDefaults);
  }, []);

  // 合并预设人格和自定义人格（排除已删除的预设）
  const filteredDefaultPersonas = defaultPersonas.filter(p => !deletedDefaultIds.includes(p.id));
  const personas = [...filteredDefaultPersonas, ...customPersonas];

  // 点击空白区域关闭下拉框
  useEffect(() => {
    const handleClickOutside = () => {
      setShowModelSelector(false);
    };
    
    if (showModelSelector) {
      document.addEventListener('click', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showModelSelector]);

  // 创建新人格
  const handleCreatePersona = (persona: { icon: string; name: string; model: string; description: string }) => {
    const newPersona = {
      ...persona,
      id: `custom-${Date.now()}`,
      role: "自定义人格",
    };
    const updated = [...customPersonas, newPersona];
    setCustomPersonas(updated);
    localStorage.setItem("customPersonas", JSON.stringify(updated));
  };

  const handleSubmit = () => {
    if (input.trim() || selectedImage) {
      // 跳转到画布页，并传递初始消息、图片和选中的人格
      navigate("/chat", { 
        state: { 
          initialMessage: input, 
          selectedModel: selectedModel.id,
          persona: selectedPersona, // 传递完整的人格信息
          selectedImage: selectedImage
        } 
      });
    }
  };

  // 未登录状态：显示 Landing Page
  if (!auth.user) {
    return (
      <div className="min-h-screen bg-white">
        {/* 导航栏 */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14">
              {/* Logo */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-white" />
                </div>
                <span className="text-lg font-semibold text-gray-900">Persona AI</span>
              </div>

              {/* Right: Login/Register */}
              <div className="flex items-center gap-3">
                <Link
                  to="/auth"
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition"
                >
                  {t('nav.signIn')}
                </Link>
                <Link
                  to="/auth"
                  className="px-5 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-medium"
                >
                  {t('home.createPersona')}
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section - 首屏 */}
        <main className="pt-32 pb-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            {/* 主标题 */}
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              The platform to create<br />
              <span className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
                AI personas
              </span>
            </h1>

            {/* 副标题 */}
            <p className="text-xl text-gray-500 mb-10 max-w-2xl mx-auto">
              Build, customize, and deploy AI personas that understand your needs. 
              Stop configuring, start creating.
            </p>

            {/* CTA 按钮 */}
            <div className="flex items-center justify-center gap-4 mb-12">
              <Link
                to="/auth"
                className="px-8 py-3.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-medium text-base"
              >
                Start free →
              </Link>
              <button className="px-8 py-3.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium text-base">
                Watch demo
              </button>
            </div>

            {/* 社会证明 */}
            <div className="flex items-center justify-center gap-6 text-sm text-gray-500 flex-wrap">
              <div className="flex items-center gap-2">
                <span>🚀</span>
                <span>Powered by DeepSeek V3</span>
              </div>
              <div className="w-px h-4 bg-gray-200" />
              <div className="flex items-center gap-2">
                <span>⚡</span>
                <span>Sub-second response</span>
              </div>
              <div className="w-px h-4 bg-gray-200" />
              <div className="flex items-center gap-2">
                <span>🌍</span>
                <span>Global deployment</span>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-gray-100 py-8 px-6">
          <div className="max-w-7xl mx-auto flex items-center justify-center text-sm text-gray-500">
            <div>© 2026 Persona AI. All rights reserved.</div>
          </div>
        </footer>
      </div>
    );
  }

  // 已登录状态：显示完整功能页面
  return (
    <div className="min-h-screen bg-white">
      <TopNav user={auth.user} onSignOut={auth.user ? auth.signOut : undefined} />

      {/* Main Content */}
      <main className="pt-20 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Announcement Banner */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full text-sm">
              <span className="text-purple-600">🎉</span>
              <span className="text-gray-600">{t('home.announcement')}</span>
              <Link to="/" className="text-purple-600 font-medium hover:underline">{t('home.tryNow')}</Link>
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-3xl font-semibold text-gray-900">{t('home.title')}</h1>
            </div>
            <p className="text-gray-500">{t('home.subtitle')}</p>
          </div>

          {/* Main Input */}
          <div className="bg-white border border-gray-200 rounded-3xl shadow-sm p-4 mb-6">
            {/* 选中的人格胶囊和图片预览 */}
            {(selectedPersona || selectedImage) && (
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {selectedPersona && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 rounded-full text-sm">
                    <span>{selectedPersona.icon}</span>
                    <span className="font-medium text-gray-800">{selectedPersona.name}</span>
                    <button 
                      onClick={() => setSelectedPersona(null)}
                      className="w-4 h-4 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedImage && (
                  <div className="relative inline-block">
                    <img
                      src={selectedImage.url}
                      alt={t('common.preview')}
                      className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      onClick={() => setSelectedImage(null)}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 text-xs"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            )}
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('home.inputPlaceholder')}
              className="w-full min-h-[80px] resize-none text-gray-900 placeholder-gray-400 outline-none text-base"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2">
                {/* Model Selector */}
                <div className="relative">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowModelSelector(!showModelSelector);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-gray-100 text-sm text-gray-600 transition"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>{selectedModel.name}</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  {showModelSelector && (
                    <div 
                      onClick={(e) => e.stopPropagation()}
                      className="absolute left-0 top-full mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-10"
                    >
                      {models.map((model) => (
                        <button
                          key={model.id}
                          onClick={() => {
                            if (model.available) {
                              setSelectedModel(model);
                              setShowModelSelector(false);
                            }
                          }}
                          disabled={!model.available}
                          className={`w-full text-left px-3 py-2 transition ${
                            model.available ? "hover:bg-gray-50" : "opacity-50 cursor-not-allowed"
                          } ${
                            selectedModel.id === model.id ? "bg-gray-50" : ""
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{model.name}</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded whitespace-nowrap ${
                                model.tagKey === "models.imageUnderstanding" 
                                  ? "bg-purple-100 text-purple-600" 
                                  : "bg-green-100 text-green-600"
                              }`}>
                                {t(model.tagKey)}
                              </span>
                            </div>
                            {!model.available && (
                              <span className="text-xs text-gray-400">{t('common.notConfigured')}</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* 图片上传按钮 */}
                <ImageUpload
                  onImageSelect={(imageData) => setSelectedImage(imageData)}
                  disabled={false}
                />
              </div>
              <div className="flex items-center gap-2">
                <button className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 transition">
                  <Lightbulb className="w-4 h-4" />
                </button>
                <button 
                  onClick={handleSubmit}
                  className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center hover:bg-gray-800 transition"
                >
                  <Sparkles className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Personas */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('home.selectPersona')}</h2>
              {personas.length > 0 && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('home.addPersona')}</span>
                </button>
              )}
            </div>
            
            {personas.length === 0 ? (
              // 没有角色时显示醒目的添加按钮
              <button
                onClick={() => setShowCreateModal(true)}
                className="w-full py-12 border-2 border-dashed border-gray-300 rounded-2xl flex flex-col items-center justify-center gap-3 hover:border-blue-400 hover:bg-blue-50/50 transition"
              >
                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center">
                  <Plus className="w-7 h-7 text-gray-400" />
                </div>
                  <div className="text-center">
                  <div className="font-medium text-gray-700 mb-1">{t('home.createFirstPersona')}</div>
                  <div className="text-sm text-gray-400">{t('home.createFirstHint')}</div>
                </div>
              </button>
            ) : (
              // 有角色时显示网格
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {personas.map((persona, index) => (
                  <button
                    key={persona.id || index}
                    onClick={() => setSelectedPersona(selectedPersona?.id === persona.id ? null : persona)}
                    className={`text-left p-4 rounded-2xl transition group ${
                      selectedPersona?.id === persona.id 
                        ? "bg-blue-50 border-2 border-blue-200" 
                        : "bg-gray-50 hover:bg-gray-100 border-2 border-transparent"
                    }`}
                  >
                    {/* 头像：支持 emoji 或 AI 生成的图片 */}
                    <div className="mb-2">
                      {persona.icon.startsWith("http") || persona.icon.startsWith("data:") ? (
                        <img 
                          src={persona.icon} 
                          alt={persona.name}
                          className="w-8 h-8 rounded-lg object-cover"
                        />
                      ) : (
                        <span className="text-2xl">{persona.icon}</span>
                      )}
                    </div>
                    <div className={`font-medium mb-1 ${
                      selectedPersona?.id === persona.id ? "text-blue-700" : "text-gray-900"
                    }`}>{persona.name}</div>
                    <div className={`text-xs mb-2 ${
                      selectedPersona?.id === persona.id ? "text-blue-500" : "text-purple-600"
                    }`}>{persona.role}</div>
                    <p className={`text-xs line-clamp-2 ${
                      selectedPersona?.id === persona.id ? "text-blue-600" : "text-gray-500"
                    }`}>{persona.description}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Recent Chats */}
          {recentChats.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('home.recentChats')}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {recentChats.map((chat) => (
                  <div
                    key={chat.id}
                    className="aspect-[4/3] bg-gray-50 rounded-2xl p-4 hover:bg-gray-100 transition group relative"
                  >
                    <Link
                      to="/chat"
                      className="block h-full"
                    >
                      <div className="h-full flex flex-col">
                        <h3 className="font-medium text-gray-900 mb-1 line-clamp-1">{chat.title}</h3>
                        <p className="text-xs text-gray-500 line-clamp-2 flex-1">{chat.preview}</p>
                        <span className="text-xs text-gray-400 mt-2">{chat.date}</span>
                      </div>
                    </Link>
                    {/* 删除按钮 */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeleteConfirm({ id: chat.id, title: chat.title });
                      }}
                      className="absolute top-2 right-2 w-7 h-7 bg-white/80 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-50 text-gray-400 hover:text-red-500 transition shadow-sm"
                      title={t('home.deleteChat')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* 创建人格弹窗 */}
      <CreatePersonaModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreatePersona}
      />

      {/* 删除确认弹窗 */}
      <ConfirmDialog
        isOpen={deleteConfirm !== null}
        title={t('home.deleteChat')}
        message={t('home.deleteChatConfirm', { title: deleteConfirm?.title || '' })}
        onConfirm={() => {
          if (deleteConfirm) {
            setRecentChats(recentChats.filter(c => c.id !== deleteConfirm.id));
          }
          setDeleteConfirm(null);
        }}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
}
